import { useState } from 'react'
import { cn } from 'components/UI/cn'
import { MetricCard } from 'components/FbUI/MetricCard'
import FbButton from 'components/FbUI/FbButton'
import {
  PiCheckCircleBold,
  PiClockCountdown,
  PiCurrencyDollar,
  PiShoppingCartSimple,
} from 'react-icons/pi'
import { PurchaseCreditsModal } from 'components/Modals/PurchaseCreditsModal'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useHasUnlimitedCredits } from '../../hooks/useHasUnlimitedCredits'
import { useFeatureFlagService } from '../../utils/useFeatureFlagService'
import { useAuth } from 'context/authentication/useAuth'
import { useCreditBalance } from '../../hooks/useCreditBalance'
import { formatInteger } from 'utils/formatting'

interface CreditBalanceCardProps {
  className?: string
  /** Suppress the internal Buy Credits button. Use when the surrounding page
   *  renders <BuyCreditsButton /> elsewhere (e.g. inline with tabs). */
  hideBuyButton?: boolean
}

/**
 * Stand-alone Buy Credits CTA. Encapsulates the billing-config check, toast,
 * and purchase modal so the same behavior can render inside CreditBalanceCard
 * or independently (e.g. inline with the Contacts page tabs).
 */
export function BuyCreditsButton({ className }: { className?: string }) {
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const { user } = useAuth()
  const navigate = useNavigate()
  const hasUnlimitedCredits = useHasUnlimitedCredits()
  const hasStripeCustomerId = !!user?.company?.stripe_customer_id

  if (hasUnlimitedCredits) return null

  const handleClick = () => {
    if (!hasStripeCustomerId) {
      toast.warning(
        'Please configure billing in settings before purchasing credits.',
        {
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          onClick: () => navigate('/settings/billing'),
        }
      )
      return
    }
    setShowPurchaseModal(true)
  }

  return (
    <>
      <FbButton
        className={cn(
          '!bg-brand-bright !text-white hover:!bg-brand-active inline-flex items-center justify-center gap-2',
          className
        )}
        onClick={handleClick}
      >
        <PiShoppingCartSimple size={18} />
        Buy Credits
      </FbButton>
      <PurchaseCreditsModal
        open={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
      />
    </>
  )
}

interface CreditStat {
  name: string
  key: 'total' | 'available' | 'used' | 'pending'
  tooltip: string
  icon: React.ReactNode
}

export function CreditBalanceCard({
  className = '',
  hideBuyButton = false,
}: CreditBalanceCardProps) {
  const { enableContactCredits } = useFeatureFlagService()
  const hasUnlimitedCredits = useHasUnlimitedCredits()
  const { credits, isLoading } = useCreditBalance()

  const creditStats: CreditStat[] = [
    {
      name: 'Credits Used',
      key: 'used' as const,
      tooltip: 'Total number of credits spent on completed contact requests',
      icon: <PiCheckCircleBold size={18} />,
    },
    ...(hasUnlimitedCredits
      ? []
      : [
          {
            name: 'Credits Pending',
            key: 'pending' as const,
            tooltip: 'Credits reserved for in-progress contact requests',
            icon: <PiClockCountdown size={18} />,
          },
        ]),
    ...(enableContactCredits
      ? [
          {
            name: 'Credits Remaining',
            key: 'available' as const,
            tooltip: 'Number of credits you can use for new contact requests',
            icon: <PiCurrencyDollar size={18} />,
          },
        ]
      : []),
  ]

  return (
    <div className={`flex flex-col relative ${className}`}>
      <div
        className={cn('flex items-center gap-2.5', !hideBuyButton && 'mb-2')}
        data-stats-container="true"
      >
        {creditStats.map((stat) => (
          <MetricCard
            key={stat.key}
            icon={stat.icon}
            label={stat.name}
            tooltipContent={stat.tooltip}
            value={
              isLoading
                ? '—'
                : hasUnlimitedCredits && stat.key === 'available'
                  ? '∞'
                  : formatInteger(credits[stat.key])
            }
          />
        ))}
      </div>
      {!hideBuyButton && !hasUnlimitedCredits && (
        <div className="relative z-10">
          <BuyCreditsButton className="w-full" />
        </div>
      )}
    </div>
  )
}
